<template>
    <div>
        <template v-if="loading">
            <x-container fluid>
                <x-layout>
                    <x-flex xs12>
                        <loading-spinner class="compact"/>
                    </x-flex>
                </x-layout>
            </x-container>
        </template>

        <template v-else>
            <x-container fluid pa-0>
                <x-layout v-for="(section, index) in value" :key="`information-section-${section.key}`">
                    <x-flex xs12>
                        <x-container fluid class="question-set-questions-container" mb-3 :mt-5="index !== 0">
                            <x-layout>
                                <x-flex>
                                    <x-radio-group
                                        v-model="section.selected_name"
                                        class="required"
                                        :row="true"
                                        hide-details
                                        :rules="[
                                            v => ruleRequired(v, 'Section name')
                                        ]"
                                        @change="setSectionName(section)"
                                    >
                                        <template v-slot:label>
                                            Section name
                                        </template>

                                        <x-radio v-for="sectionName in sectionNames" :key="sectionName" :label="sectionName" :value="sectionName" />
                                    </x-radio-group>
                                </x-flex>

                                <x-flex class="section-controls grow-0">
                                    <x-btn
                                        icon
                                        :disabled="index === 0"
                                        @click="moveSectionDown(index)"
                                    >
                                        <x-icon small>arrow_upward</x-icon>
                                    </x-btn>

                                    <x-btn
                                        icon
                                        :disabled="index + 1 === value.length"
                                        @click="moveSectionUp(index)"
                                    >
                                        <x-icon small>arrow_downward</x-icon>
                                    </x-btn>

                                    <x-btn
                                        class="remove-section-button"
                                        icon
                                        @click="removeSection(index)"
                                    >
                                        <x-icon small>delete_outline</x-icon>
                                    </x-btn>
                                </x-flex>
                            </x-layout>

                            <x-layout v-if="section.selected_name === 'Other'" class="nested">
                                <x-flex xs6>
                                    <x-text-field
                                        v-model="section.name"
                                        class="input-no-margin required"
                                        :rules="[v => ruleRequired(v, 'Section name')]"
                                        outline
                                        hide-details
                                    />
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex xs6>
                                    <x-select
                                        label="Viewable by"
                                        :items="insurers"
                                        v-model="section.insurer_company_ids"
                                        placeholder="Viewable by"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        item-text="name"
                                        item-value="id"
                                        multiple
                                        hide-details
                                        :menu-props="{
                                            offsetY: true,
                                            maxHeight: 400
                                        }"
                                    >
                                        <template slot="selection" slot-scope="{item, index}">
                                            <span v-if="section.insurer_company_ids.length === 1">{{ item.name }}</span>
                                            <span v-else-if="index === 0">{{ numberOfInsurersSelected(section.insurer_company_ids) }}</span>
                                        </template>

                                        <template slot="prepend-item">
                                            <x-list-tile class="buttons-top-tile no-hover">
                                                <x-btn @click="selectAllInsurers(section)" color="secondary">Select all</x-btn>
                                                <x-btn @click="deselectAllInsurers(section)" color="tertiary">Deselect all</x-btn>
                                            </x-list-tile>
                                        </template>
                                    </x-select>
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex xs12>
                                    <text-editor
                                        v-model="section.content"
                                        label="Section content"
                                        required
                                        :rules="[v => ruleRequired(v, 'Section content')]"
                                    />
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>

                <x-layout>
                    <x-flex xs12 text-xs-center>
                        <x-btn
                            class="wide my-3"
                            color="secondary"
                            @click="addSection"
                        >
                            <i class="material-icons mr-2">add</i>

                            Add {{ value.length > 0 ? 'another' : 'a' }} section
                        </x-btn>
                    </x-flex>
                </x-layout>
            </x-container>
        </template>

        <modal
            ref="confirmRemoveSection"
            title="Remove section"
            confirmation
            negative
            action-text="Yes, remove section"
        >
            <p>Removing this section will lose the contained information, are you sure?</p>
        </modal>
    </div>
</template>

<script>
    import TextEditor from '~/components/Shared/UIComponents/Editor/TextEditor';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import rules from '~/mixins/rules';

    export default {
        name: 'additional-information-edit',
        components: {
            TextEditor,
            Modal
        },
        mixins: [rules],
        props: {
            value: Array,
            facilityId: Number,
            insurerList: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                loading: true,
                sectionNames: [
                    'Interest',
                    'Premium',
                    'Prior experience',
                    'Other'
                ],
                blankSection: {
                    selected_name: null,
                    name: null,
                    insurer_company_ids: [],
                    content: null
                },
                insurers: [],
                nextKey: 1
            }
        },
        watch: {
            insurerList() {
                this.getInsurers();
            },
            value() {
                this.getInsurers(true);
            }
        },
        mounted() {
            this.getInsurers(true);
        },
        methods: {
            initialiseSections() {
                this.value.forEach(section => {
                    section.key = this.nextKey;
                    this.nextKey++;

                    if (this.sectionNames.includes(section.name)) {
                        section.selected_name = section.name;
                    } else {
                        section.selected_name = 'Other';
                    }

                    this.$set(section, 'insurer_company_ids', section.permissions.map(permission => permission.insurer_company_id).filter(insurerId => {
                            return this.insurers.find(insurer => insurer.id === insurerId);
                        })
                    );
                });
            },
            addSection(index) {
                if (typeof index === 'number') {
                    this.value.splice(index, 0, {
                        key: this.nextKey,
                        ...this.blankSection
                    });
                } else {
                    this.value.push(
                        {
                            key: this.nextKey,
                            ...this.blankSection
                        }
                    );
                }

                this.nextKey++;
            },
            moveSectionDown(index) {
                if (index > 0) {
                    this.value.splice(index - 1, 0, this.value.splice(index, 1)[0]);
                }
            },
            moveSectionUp(index) {
                if (index + 1 < this.value.length) {
                    this.value.splice(index + 1, 0, this.value.splice(index, 1)[0]);
                }
            },
            async removeSection(index) {
                if (await this.$refs.confirmRemoveSection.open() === true) {
                    this.value.splice(index, 1);
                }
            },
            setSectionName(section) {
                if (section.selected_name === 'Other') {
                    section.name = '';
                } else {
                    section.name = section.selected_name;
                }
                this.$forceUpdate();
            },
            async getInsurers(initialLoad = false) {
                if (this.insurerList.length > 0) {
                    this.fillDefaultInsurers(this.insurerList);
                } else {
                    const url = (this.facilityId !== null) ? `/api/insurer/facility/${this.facilityId}/captive/` : '/api/insurer';
                    await axios.get(url).then(res => {
                        this.fillDefaultInsurers(res.data.data);
                    });
                }

                if (initialLoad) {
                    this.initialiseSections();
                    this.loading = false;
                }
            },
            fillDefaultInsurers(insurerList) {
                this.insurers = insurerList;
                this.blankSection.insurer_company_ids = this.insurers.map(insurer => insurer.id);
            },
            numberOfInsurersSelected(selectedInsurers) {
                if (this.insurers.length === selectedInsurers.length) {
                    return 'All insurers selected';
                }

                return selectedInsurers.length + ' insurers selected';
            },
            selectAllInsurers(section) {
                section.insurer_company_ids = this.insurers.map(insurer => insurer.id);
            },
            deselectAllInsurers(section) {
                section.insurer_company_ids = [];
            },
        }
    }
</script>

<style scoped lang="scss">
.section-controls {
    .x-btn {
        background: transparent !important;
        border: none !important;
        color: $color-white !important;
        opacity: 0.8;

        &.remove-section-button {
            color: #FE585F !important;
            opacity: 1;

            &:hover {
                color: #FE585F !important;
            }
        }

        &:hover {
            background: transparent !important;
            color: $color-white !important;
            opacity: 1;

            &:before {
                background: transparent !important;
            }
        }
    }
}
</style>
